import { useEffect, useRef, type MutableRefObject } from 'react'
import { io, Socket } from 'socket.io-client'
import { getAccessToken } from '@/lib/session'

const API_URL = import.meta.env.VITE_API_URL

type SocketPayload = Record<string, any>

export type ChatSocketHandlers = {
  onResync?: () => void
  onMessage?: (payload: SocketPayload) => void
  onMessageSeen?: (payload: SocketPayload) => void
  onMessageDelivered?: (payload: SocketPayload) => void
  onMessageTranscript?: (payload: SocketPayload) => void
  onTyping?: (payload: SocketPayload) => void
  onConversationCreated?: (payload: SocketPayload) => void
  onConversationAssigned?: (payload: SocketPayload) => void
  onConversationRead?: (payload: SocketPayload) => void
  onConversationVisitorRead?: (payload: SocketPayload) => void
  onConversationFinalized?: (payload: SocketPayload) => void
  onConversationReopened?: (payload: SocketPayload) => void
  onConversationPresence?: (payload: SocketPayload) => void
  onConversationBot?: (payload: SocketPayload) => void
  onNameUpdateSuggested?: (payload: SocketPayload) => void
  onNameUpdated?: (payload: SocketPayload) => void
  onNameUpdateDismissed?: (payload: SocketPayload) => void
}

type HandlerKey = keyof ChatSocketHandlers

const EVENTS: [string, Exclude<HandlerKey, 'onResync'>][] = [
  ['message:new', 'onMessage'],
  ['message:seen', 'onMessageSeen'],
  ['message:delivered', 'onMessageDelivered'],
  ['message:transcript', 'onMessageTranscript'],
  ['typing', 'onTyping'],
  ['conversation:created', 'onConversationCreated'],
  ['conversation:assigned', 'onConversationAssigned'],
  ['conversation:read', 'onConversationRead'],
  ['conversation:visitor-read', 'onConversationVisitorRead'],
  ['conversation:finalized', 'onConversationFinalized'],
  ['conversation:reopened', 'onConversationReopened'],
  ['conversation:presence', 'onConversationPresence'],
  ['conversation:bot', 'onConversationBot'],
  ['conversation:name-update-suggested', 'onNameUpdateSuggested'],
  ['conversation:name-updated', 'onNameUpdated'],
  ['conversation:name-update-dismissed', 'onNameUpdateDismissed']
]

export type ChatSocketBus = {
  emit: (key: HandlerKey, payload?: SocketPayload) => void
  subscribe: (handlers: MutableRefObject<ChatSocketHandlers>) => () => void
}

export function createChatSocketBus(): ChatSocketBus {
  const subscribers = new Set<MutableRefObject<ChatSocketHandlers>>()
  return {
    emit: (key, payload) => {
      subscribers.forEach(ref => (ref.current[key] as ((p: SocketPayload) => void) | undefined)?.(payload ?? {}))
    },
    subscribe: ref => {
      subscribers.add(ref)
      return () => {
        subscribers.delete(ref)
      }
    }
  }
}

export function useChatSocketBus(bus: ChatSocketBus | null | undefined, handlers: ChatSocketHandlers) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!bus) return
    return bus.subscribe(handlersRef)
  }, [bus])
}

export function useChatSocket({
  companyId,
  conversationId,
  bus,
  ...handlers
}: { companyId?: string; conversationId?: string | null; bus?: ChatSocketBus } & ChatSocketHandlers) {
  const socketRef = useRef<Socket | null>(null)
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const busRef = useRef(bus)
  busRef.current = bus
  const conversationRef = useRef(conversationId ?? null)
  conversationRef.current = conversationId ?? null

  useEffect(() => {
    if (!companyId) return
    const credentials = () => ({ token: getAccessToken() ?? '', companyId })
    const socket = io(API_URL, { path: '/chat/socket', transports: ['websocket'], query: credentials() })
    const refreshCredentials = () => {
      socket.io.opts.query = credentials()
    }
    socket.io.on('reconnect_attempt', refreshCredentials)
    socketRef.current = socket

    const dispatch = (key: HandlerKey, payload?: SocketPayload) => {
      ;(handlersRef.current[key] as ((p: SocketPayload) => void) | undefined)?.(payload ?? {})
      busRef.current?.emit(key, payload)
    }

    const handleConnect = () => {
      socket.emit('agency:join', { companyId })
      if (conversationRef.current) socket.emit('conversation:join', { conversationId: conversationRef.current })
      dispatch('onResync')
    }

    socket.on('connect', handleConnect)
    for (const [event, key] of EVENTS) socket.on(event, (payload: SocketPayload) => dispatch(key, payload))

    return () => {
      if (conversationRef.current && socket.connected) socket.emit('conversation:leave', { conversationId: conversationRef.current })
      socket.io.off('reconnect_attempt', refreshCredentials)
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
    }
  }, [companyId])

  useEffect(() => {
    const socket = socketRef.current
    if (!socket || !conversationId) return
    if (socket.connected) socket.emit('conversation:join', { conversationId })
    return () => {
      if (socket.connected) socket.emit('conversation:leave', { conversationId })
    }
  }, [companyId, conversationId])

  return socketRef
}
